'use client'

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdSecurity,
    MdSettings,
    MdRefresh,
    MdSend,
    MdDownload,
    MdListAlt,
} from 'react-icons/md'
import {
    getSngpcConfiguration,
    getSngpcMovements,
    getSngpcBatches,
    getSngpcBalance,
    generateSngpcBatch,
    updateSngpcConfiguration,
    SngpcConfiguration,
    SngpcMovement,
    SngpcTransmissionBatch,
    SngpcBalance,
    SNGPC_MOVEMENT_TYPE_LABELS,
    SNGPC_TRANSMISSION_STATUS_LABELS,
    SNGPC_TRANSMISSION_STATUS_COLORS,
} from '@/lib/api/stock/sngpc-api'

export default function SngpcPage() {
    const [config, setConfig] = useState<SngpcConfiguration | null>(null)
    const [movements, setMovements] = useState<SngpcMovement[]>([])
    const [batches, setBatches] = useState<SngpcTransmissionBatch[]>([])
    const [balance, setBalance] = useState<SngpcBalance[]>([])
    const [loading, setLoading] = useState(true)
    const [generating, setGenerating] = useState(false)
    const [savingConfig, setSavingConfig] = useState(false)
    const [activeTab, setActiveTab] = useState<
        'balance' | 'movements' | 'batches' | 'config'
    >('balance')
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
    const [configForm, setConfigForm] = useState({
        cnes: '',
        anvsCnpj: '',
        anvsResponsavel: '',
        anvsCrf: '',
        autoGenerate: false,
        generateDay: 1,
        emailNotification: false,
        notificationEmail: '',
    })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)
        try {
            const [configRes, movementsRes, batchesRes, balanceRes] =
                await Promise.all([
                    getSngpcConfiguration(),
                    getSngpcMovements({ pending: true }),
                    getSngpcBatches(),
                    getSngpcBalance(),
                ])

            if (configRes.success) {
                setConfig(configRes.result)
                if (configRes.result) {
                    setConfigForm({
                        cnes: configRes.result.cnes || '',
                        anvsCnpj: configRes.result.anvsCnpj || '',
                        anvsResponsavel: configRes.result.anvsResponsavel || '',
                        anvsCrf: configRes.result.anvsCrf || '',
                        autoGenerate: configRes.result.autoGenerate || false,
                        generateDay: configRes.result.generateDay || 1,
                        emailNotification: configRes.result.emailNotification || false,
                        notificationEmail: configRes.result.notificationEmail || '',
                    })
                }
            }
            if (movementsRes.success) setMovements(movementsRes.result)
            if (batchesRes.success) setBatches(batchesRes.result)
            if (balanceRes.success) setBalance(balanceRes.result)
        } catch (error) {
            toast.error('Erro ao carregar dados do SNGPC')
        } finally {
            setLoading(false)
        }
    }

    const handleGenerateBatch = async () => {
        setGenerating(true)
        try {
            const response = await generateSngpcBatch(selectedMonth, selectedYear)
            if (response.success) {
                toast.success('Lote gerado com sucesso')
                loadData()
            } else {
                toast.error(response.error || 'Erro ao gerar lote')
            }
        } catch (error) {
            toast.error('Erro ao gerar lote')
        } finally {
            setGenerating(false)
        }
    }

    const handleSaveConfig = async () => {
        if (!configForm.cnes.trim()) {
            toast.error('CNES é obrigatório')
            return
        }
        if (!configForm.anvsCnpj.trim()) {
            toast.error('CNPJ ANVISA é obrigatório')
            return
        }
        if (!configForm.anvsResponsavel.trim()) {
            toast.error('Responsável é obrigatório')
            return
        }
        if (!configForm.anvsCrf.trim()) {
            toast.error('CRF é obrigatório')
            return
        }

        setSavingConfig(true)
        try {
            const response = await updateSngpcConfiguration(configForm)
            if (response.success) {
                toast.success('Configuração salva com sucesso')
                setConfig(response.result)
            } else {
                toast.error(response.error || 'Erro ao salvar configuração')
            }
        } catch (error) {
            toast.error('Erro ao salvar configuração')
        } finally {
            setSavingConfig(false)
        }
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR')
    }

    const pendingMovements = movements.filter(m => !m.transmissionBatchId)

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando SNGPC...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-warning/70 flex items-center justify-center shadow-lg">
                        <MdSecurity className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            SNGPC
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Sistema Nacional de Gerenciamento de Produtos
                            Controlados
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-ghost btn-sm gap-2"
                    onClick={loadData}
                >
                    <MdRefresh className="text-lg" />
                    Atualizar
                </button>
            </div>

            {!config && (
                <div className="alert alert-warning">
                    <MdSettings className="text-2xl" />
                    <div>
                        <h3 className="font-bold">Configuração necessária</h3>
                        <p className="text-sm">
                            Configure os dados do SNGPC antes de utilizar o
                            sistema
                        </p>
                    </div>
                    <button
                        className="btn btn-sm"
                        onClick={() => setActiveTab('config')}
                    >
                        Configurar
                    </button>
                </div>
            )}

            <div className="stats shadow bg-base-100 border border-base-300/50">
                <div className="stat">
                    <div className="stat-figure text-warning">
                        <MdListAlt className="text-3xl" />
                    </div>
                    <div className="stat-title">Movimentações Pendentes</div>
                    <div className="stat-value text-warning">
                        {pendingMovements.length}
                    </div>
                    <div className="stat-desc">Aguardando transmissão</div>
                </div>
                <div className="stat">
                    <div className="stat-figure text-secondary">
                        <MdSecurity className="text-3xl" />
                    </div>
                    <div className="stat-title">Medicamentos Controlados</div>
                    <div className="stat-value text-secondary">
                        {balance.length}
                    </div>
                    <div className="stat-desc">Sem estoque</div>
                </div>
                <div className="stat">
                    <div className="stat-figure text-success">
                        <MdSend className="text-3xl" />
                    </div>
                    <div className="stat-title">Último Envio</div>
                    <div className="stat-value text-success text-lg">
                        {batches[0]
                            ? formatDate(batches[0].createdAt)
                            : 'Nenhum'}
                    </div>
                    <div className="stat-desc">
                        {batches[0]
                            ? SNGPC_TRANSMISSION_STATUS_LABELS[batches[0].status]
                            : '-'}
                    </div>
                </div>
            </div>

            <div className="tabs tabs-boxed bg-base-100 p-2 shadow-sm border border-base-300/50">
                <button
                    className={`tab tab-lg font-medium transition-all ${activeTab === 'balance'
                        ? 'tab-active tab-active-primary !text-secondary'
                        : 'hover:bg-base-200'
                        }`}
                    onClick={() => setActiveTab('balance')}
                >
                    Saldo
                </button>
                <button
                    className={`tab tab-lg font-medium transition-all ${activeTab === 'movements'
                        ? 'tab-active tab-active-primary !text-secondary'
                        : 'hover:bg-base-200'
                        }`}
                    onClick={() => setActiveTab('movements')}
                >
                    Movimentações ({pendingMovements.length})
                </button>
                <button
                    className={`tab tab-lg font-medium transition-all ${activeTab === 'batches'
                        ? 'tab-active tab-active-primary !text-secondary'
                        : 'hover:bg-base-200'
                        }`}
                    onClick={() => setActiveTab('batches')}
                >
                    Transmissões
                </button>
                <button
                    className={`tab tab-lg font-medium transition-all ${activeTab === 'config'
                        ? 'tab-active tab-active-primary !text-secondary'
                        : 'hover:bg-base-200'
                        }`}
                    onClick={() => setActiveTab('config')}
                >
                    <MdSettings className="mr-2 text-lg" />
                    Configuração
                </button>
            </div>

            {activeTab === 'balance' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Medicamento</th>
                                    <th>Tipo Controle</th>
                                    <th>Saldo Inicial</th>
                                    <th>Entradas</th>
                                    <th>Saídas</th>
                                    <th>Saldo Atual</th>
                                </tr>
                            </thead>
                            <tbody>
                                {balance.map(item => (
                                    <tr key={item.medicationId}>
                                        <td>
                                            <div className="font-semibold">
                                                {item.medication.name}
                                            </div>
                                            {item.medication.anvisaRegistry && (
                                                <div className="text-xs text-base-content/60">
                                                    ANVISA:{' '}
                                                    {
                                                        item.medication
                                                            .anvisaRegistry
                                                    }
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <span className="badge badge-warning">
                                                {item.medication.controlType}
                                            </span>
                                        </td>
                                        <td>{item.initialBalance}</td>
                                        <td className="text-success">
                                            +{item.entries}
                                        </td>
                                        <td className="text-error">
                                            -{item.exits}
                                        </td>
                                        <td className="font-bold">
                                            {item.currentBalance}
                                        </td>
                                    </tr>
                                ))}
                                {balance.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="text-center py-8 text-base-content/60"
                                        >
                                            Nenhum medicamento controlado no
                                            estoque
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'movements' && (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            Movimentações Pendentes
                        </h2>
                        <div className="flex items-center gap-2">
                            <select
                                className="select select-bordered select-sm"
                                value={selectedMonth}
                                onChange={e =>
                                    setSelectedMonth(parseInt(e.target.value))
                                }
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {new Date(2000, i).toLocaleString(
                                            'pt-BR',
                                            { month: 'long' }
                                        )}
                                    </option>
                                ))}
                            </select>
                            <select
                                className="select select-bordered select-sm"
                                value={selectedYear}
                                onChange={e =>
                                    setSelectedYear(parseInt(e.target.value))
                                }
                            >
                                {Array.from({ length: 5 }, (_, i) => (
                                    <option
                                        key={selectedYear - 2 + i}
                                        value={selectedYear - 2 + i}
                                    >
                                        {selectedYear - 2 + i}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="btn btn-secondary btn-sm gap-2"
                                onClick={handleGenerateBatch}
                                disabled={
                                    generating || pendingMovements.length === 0
                                }
                            >
                                {generating ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdSend className="text-lg" />
                                )}
                                Gerar Lote
                            </button>
                        </div>
                    </div>
                    <div className="bg-base-100 rounded-xl border border-base-300/50">
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Tipo</th>
                                        <th>Medicamento</th>
                                        <th>Qtd</th>
                                        <th>Paciente/Fornecedor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingMovements.map(mov => (
                                        <tr key={mov.id}>
                                            <td>
                                                {formatDate(mov.createdAt)}
                                            </td>
                                            <td>
                                                <span className="badge badge-ghost">
                                                    {
                                                        SNGPC_MOVEMENT_TYPE_LABELS[
                                                        mov.type
                                                        ]
                                                    }
                                                </span>
                                            </td>
                                            <td>{mov.medication?.name}</td>
                                            <td>{mov.quantity}</td>
                                            <td>
                                                {mov.patientName ||
                                                    mov.supplierCnpj ||
                                                    '-'}
                                            </td>
                                        </tr>
                                    ))}
                                    {pendingMovements.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="text-center py-8 text-base-content/60"
                                            >
                                                Nenhuma movimentação pendente
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'batches' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Período</th>
                                    <th>Movimentações</th>
                                    <th>Status</th>
                                    <th>Data Geração</th>
                                    <th>Transmissão</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {batches.map(batch => (
                                    <tr key={batch.id}>
                                        <td>
                                            {batch.referenceMonth}/
                                            {batch.referenceYear}
                                        </td>
                                        <td>{batch.totalMovements}</td>
                                        <td>
                                            <span
                                                className={`badge ${SNGPC_TRANSMISSION_STATUS_COLORS[batch.status]}`}
                                            >
                                                {
                                                    SNGPC_TRANSMISSION_STATUS_LABELS[
                                                    batch.status
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>{formatDate(batch.createdAt)}</td>
                                        <td>
                                            {batch.transmittedAt
                                                ? formatDate(batch.transmittedAt)
                                                : '-'}
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                title="Download XML"
                                            >
                                                <MdDownload className="text-lg" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {batches.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="text-center py-8 text-base-content/60"
                                        >
                                            Nenhum lote gerado
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'config' && (
                <div className="flex flex-col gap-6">
                    {/* Dados do Estabelecimento */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold mb-4">
                            Dados do Estabelecimento
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">CNES *</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="Código CNES"
                                    value={configForm.cnes}
                                    onChange={e =>
                                        setConfigForm({
                                            ...configForm,
                                            cnes: e.target.value,
                                        })
                                    }
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Cadastro Nacional de Estabelecimentos de Saúde
                                    </span>
                                </label>
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">CNPJ ANVISA *</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="00.000.000/0000-00"
                                    value={configForm.anvsCnpj}
                                    onChange={e =>
                                        setConfigForm({
                                            ...configForm,
                                            anvsCnpj: e.target.value,
                                        })
                                    }
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        CNPJ cadastrado na ANVISA
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Responsável Técnico */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold mb-4">
                            Responsável Técnico
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Nome Completo *</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="Nome do farmacêutico responsável"
                                    value={configForm.anvsResponsavel}
                                    onChange={e =>
                                        setConfigForm({
                                            ...configForm,
                                            anvsResponsavel: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">CRF *</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="Ex: CRF-SP 12345"
                                    value={configForm.anvsCrf}
                                    onChange={e =>
                                        setConfigForm({
                                            ...configForm,
                                            anvsCrf: e.target.value,
                                        })
                                    }
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Conselho Regional de Farmácia
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Configurações de Transmissão */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold mb-4">
                            Configurações de Transmissão
                        </h2>
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between p-4 bg-base-200/50 rounded-lg">
                                <div>
                                    <p className="font-medium">Geração Automática</p>
                                    <p className="text-sm text-base-content/60">
                                        Gerar lote de transmissão automaticamente todo mes
                                    </p>
                                </div>
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={configForm.autoGenerate}
                                    onChange={e =>
                                        setConfigForm({
                                            ...configForm,
                                            autoGenerate: e.target.checked,
                                        })
                                    }
                                />
                            </div>
                            {configForm.autoGenerate && (
                                <div className="form-control max-w-xs ml-4">
                                    <label className="label">
                                        <span className="label-text">Dia do mês para geração</span>
                                    </label>
                                    <select
                                        className="select select-bordered"
                                        value={configForm.generateDay}
                                        onChange={e =>
                                            setConfigForm({
                                                ...configForm,
                                                generateDay: parseInt(e.target.value),
                                            })
                                        }
                                    >
                                        {Array.from({ length: 28 }, (_, i) => (
                                            <option key={i + 1} value={i + 1}>
                                                Dia {i + 1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="flex items-center justify-between p-4 bg-base-200/50 rounded-lg">
                                <div>
                                    <p className="font-medium">Notificação por Email</p>
                                    <p className="text-sm text-base-content/60">
                                        Receber notificação quando o lote for gerado
                                    </p>
                                </div>
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={configForm.emailNotification}
                                    onChange={e =>
                                        setConfigForm({
                                            ...configForm,
                                            emailNotification: e.target.checked,
                                        })
                                    }
                                />
                            </div>
                            {configForm.emailNotification && (
                                <div className="form-control max-w-md ml-4">
                                    <label className="label">
                                        <span className="label-text">Email para notificação</span>
                                    </label>
                                    <input
                                        type="email"
                                        className="input input-bordered"
                                        placeholder="email@exemplo.com"
                                        value={configForm.notificationEmail}
                                        onChange={e =>
                                            setConfigForm({
                                                ...configForm,
                                                notificationEmail: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={handleSaveConfig}
                            disabled={savingConfig}
                        >
                            {savingConfig ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdSettings className="text-lg" />
                            )}
                            Salvar Configuração
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
